/*
 * Simple Cookie Manager (TypeScript)
 * A lightweight utility for reading, writing, and managing browser cookies.
 * Created fresh on March 05, 2025, with no external dependencies or licensing restrictions.
 *
 * Methods:
 * - cookieManager.get(name)
 * - cookieManager.set(name, value[, expires[, path[, domain[, secure]]]])
 * - cookieManager.remove(name[, path[, domain]])
 * - cookieManager.exists(name)
 * - cookieManager.list()
 */

const cookieManager = {
    // Get a cookie value by name
    get(name: string): string | null {
        if (!name) return null;
        const match = document.cookie.match(
            new RegExp(`(?:^|;\\s*)${encodeURIComponent(name)}=([^;]*)`)
        );
        return match ? decodeURIComponent(match[1]) : null;
    },

    // Set a cookie with optional expiration, path, domain, and secure flag
    set(
        name: string,
        value: string,
        expires?: number | Date | string,
        path?: string,
        domain?: string,
        secure?: boolean
    ): boolean {
        if (!name || !value) return false;

        let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

        // Handle expiration
        if (expires) {
            if (typeof expires === 'number') {
                // Expires as seconds from now
                const date = new Date(Date.now() + expires * 1000);
                cookieString += `; expires=${date.toUTCString()}`;
            } else if (expires instanceof Date) {
                cookieString += `; expires=${expires.toUTCString()}`;
            } else if (typeof expires === 'string') {
                cookieString += `; expires=${expires}`;
            }
        }

        // Add optional attributes
        if (path) cookieString += `; path=${path}`;
        if (domain) cookieString += `; domain=${domain}`;
        if (secure) cookieString += '; secure';

        document.cookie = cookieString;
        return true;
    },

    // Remove a cookie by setting it to expire in the past
    remove(name: string, path?: string, domain?: string): boolean {
        if (!this.exists(name)) return false;

        let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        if (path) cookieString += `; path=${path}`;
        if (domain) cookieString += `; domain=${domain}`;

        document.cookie = cookieString;
        return true;
    },

    // Check if a cookie exists
    exists(name: string): boolean {
        if (!name) return false;
        return new RegExp(`(?:^|;\\s*)${encodeURIComponent(name)}=`).test(document.cookie);
    },

    // List all cookie names
    list(): string[] {
        if (!document.cookie) return [];
        return document.cookie
            .split(';')
            .map(cookie => decodeURIComponent(cookie.split('=')[0].trim()));
    },
};

export default cookieManager;
